import { LogManager } from '@src/utility/Logger';

/**
 * PresentationTimeFrameConversionService
 *
 * Encapsulates time-to-frame conversions with precision adjustments for:
 * - Navigation: Ensures playback lands on the intended frame
 * - Rendering: Prevents Creatomate from selecting the next frame due to truncation
 *
 * Stateless service with no dependencies.
 */

const logger = LogManager.getLogger(
  'service:PresentationTimeFrameConversionService',
);

/**
 * Timestamp precision for rendering (Creatomate uses 4 decimals)
 */
const TIMESTAMP_PRECISION = 4;

/**
 * Navigation adjustment: adds 4% of frame duration to ensure landing on intended frame
 */
const NAVIGATION_FRAME_OFFSET_PERCENT = 0.04;

/**
 * Rendering adjustment: subtracts 2% of frame duration to avoid rounding up to next frame
 */
const RENDERING_FRAME_OFFSET_PERCENT = 0.02;

/**
 * Preview adjustment: no offset — the browser-based preview player does not
 * benefit from frame-boundary adjustments (see PLAT-9291 root cause analysis).
 * We still truncate to TIMESTAMP_PRECISION for consistency.
 */
const PREVIEW_FRAME_OFFSET_PERCENT = -0.01;

export class PresentationTimeFrameConversionService {
  /**
   * Convert time (seconds) to frame number
   * @param time - Time in seconds
   * @param frameRate - Frames per second
   * @returns Frame number (rounded)
   */
  timeToFrame(time: number, frameRate: number): number {
    if (time < 0) return 0;
    if (frameRate <= 0) {
      logger.warn('Invalid frameRate for timeToFrame', { frameRate });
      return 0;
    }
    return Math.round(time * frameRate);
  }

  /**
   * Convert frame number to time (seconds)
   * @param frame - Frame number
   * @param frameRate - Frames per second
   * @returns Time in seconds
   */
  frameToTime(frame: number, frameRate: number): number {
    if (frameRate <= 0) {
      logger.warn('Invalid frameRate for frameToTime', { frameRate });
      return 0;
    }
    if (frame < 0) return 0;
    return frame / frameRate;
  }

  /**
   * Adjust time for navigation/playback positioning
   *
   * Adds a small offset (4% of frame duration) to ensure the video player
   * lands on the intended frame rather than the previous frame due to
   * rounding/truncation issues during playback.
   *
   * @param time - Target time in seconds
   * @param frameRate - Frames per second
   * @returns Adjusted time with navigation offset
   */
  adjustTimeForNavigation(time: number, frameRate: number): number {
    if (frameRate <= 0) {
      logger.warn('Invalid frameRate for adjustTimeForNavigation', {
        frameRate,
      });
      return time;
    }

    const targetFrame = Math.max(0, Math.round(time * frameRate));
    const frameDuration = 1 / frameRate;
    const adjustment = frameDuration * NAVIGATION_FRAME_OFFSET_PERCENT;
    return Math.max(0, targetFrame / frameRate + adjustment);
  }

  /**
   * Adjust time for rendering output (Creatomate)
   *
   * Subtracts a small offset (2% of frame duration) and truncates to 4 decimal
   * places to prevent Creatomate from selecting the next frame due to rounding
   * up issues. This is applied to video trim_start and keyframe times.
   *
   * @param time - Target time in seconds
   * @param frameRate - Frames per second
   * @returns Adjusted time with rendering offset and precision truncation
   */
  adjustTimeForRendering(time: number, frameRate: number): number {
    if (frameRate <= 0) {
      logger.warn('Invalid frameRate for adjustTimeForRendering', {
        frameRate,
      });
      return time;
    }

    const frameDuration = 1 / frameRate;
    const targetFrameNumber = Math.round(time / frameDuration);
    const adjustment = frameDuration * RENDERING_FRAME_OFFSET_PERCENT;

    const adjustedTime =
      Math.trunc(
        (targetFrameNumber * frameDuration - adjustment) *
          10 ** TIMESTAMP_PRECISION,
      ) /
      10 ** TIMESTAMP_PRECISION;

    return Math.max(0, adjustedTime);
  }

  /**
   * Adjust time for preview playback (Creatomate preview iframe).
   *
   * Truncates to `TIMESTAMP_PRECISION` decimals for consistency but applies
   * **no offset** (`PREVIEW_FRAME_OFFSET_PERCENT = 0`).  The browser-based
   * preview player is seconds-based, not frame-based, so sub-frame offset
   * adjustments cannot eliminate flash frames there (see PLAT-9291).
   *
   * @param time - Target time in seconds
   * @param frameRate - Frames per second
   * @returns Time truncated to 4 decimal places, no offset
   */
  adjustTimeForPreview(time: number, frameRate: number): number {
    if (frameRate <= 0) {
      logger.warn('Invalid frameRate for adjustTimeForPreview', {
        frameRate,
      });
      return time;
    }

    const frameDuration = 1 / frameRate;
    const targetFrameNumber = Math.round(time / frameDuration);
    const adjustment = frameDuration * PREVIEW_FRAME_OFFSET_PERCENT;

    const adjustedTime =
      Math.trunc(
        (targetFrameNumber * frameDuration - adjustment) *
          10 ** TIMESTAMP_PRECISION,
      ) /
      10 ** TIMESTAMP_PRECISION;

    return Math.max(0, adjustedTime);
  }
}
